import { useState } from 'react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import { formatDate } from '@/utils/formatters';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Shield } from 'lucide-react';
import type { Company } from './types';

interface CompanyFormData {
    name: string;
    tax_id: string;
    registration_no: string;
    address: string;
    is_active: boolean;
}

interface CompaniesTabProps {
    companies: Company[];
    loading: boolean;
    onSelect: (id: string) => void;
    onCreated: () => void;
}

const emptyFormData: CompanyFormData = {
    name: '',
    tax_id: '',
    registration_no: '',
    address: '',
    is_active: true,
};

const CompaniesTab = ({ companies, loading, onSelect, onCreated }: CompaniesTabProps) => {
    const { t } = useI18n();
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState<CompanyFormData>(emptyFormData);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const openForm = () => {
        setShowForm(true);
        setFormData(emptyFormData);
        setFormErrors({});
        setSubmitError(null);
    };

    const closeForm = () => {
        setShowForm(false);
        setFormData(emptyFormData);
        setFormErrors({});
        setSubmitError(null);
    };

    const handleInputChange = (field: keyof CompanyFormData, value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};
        if (!formData.name.trim()) errors.name = t('forms.required', 'common');
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            const payload = {
                name: formData.name.trim(),
                tax_id: formData.tax_id.trim() || undefined,
                registration_no: formData.registration_no.trim() || undefined,
                address: formData.address.trim() || undefined,
                is_active: formData.is_active,
            };

            const resp = await apiFetch('/api/companies/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                setSubmitError(t('finance.companies.createFailed', 'admin'));
                return;
            }

            closeForm();
            onCreated();
        } catch (err) {
            console.error('Error creating company', err);
            setSubmitError(t('finance.companies.createFailed', 'admin'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{t('finance.companies.title', 'admin')}</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('finance.companies.description', 'admin')}
                    </p>
                </div>
                <Button onClick={openForm} className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    {t('finance.companies.add', 'admin')}
                </Button>
            </div>

            {loading ? (
                <div>{t('actions.loading', 'common')}</div>
            ) : (
                <div className="border rounded overflow-hidden">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.name', 'admin')}</th>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.taxId', 'admin')}</th>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.registrationNo', 'admin')}</th>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.status', 'admin')}</th>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.nav', 'admin')}</th>
                                <th className="px-3 py-2 text-left">{t('finance.companies.table.created', 'admin')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.map((company) => (
                                <tr
                                    key={company.id}
                                    className="border-t hover:bg-gray-50 cursor-pointer"
                                    onClick={() => onSelect(company.id)}
                                >
                                    <td className="px-3 py-2">{company.name}</td>
                                    <td className="px-3 py-2">{company.tax_id || '-'}</td>
                                    <td className="px-3 py-2">{company.registration_no || '-'}</td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${company.is_active
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                }`}
                                        >
                                            {company.is_active
                                                ? t('status.active', 'common')
                                                : t('status.inactive', 'common')}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center space-x-2">
                                            <Shield
                                                className={`h-4 w-4 ${company.has_nav_credentials ? 'text-green-600' : 'text-gray-400'}`}
                                            />
                                            <Badge variant={company.has_nav_credentials ? 'default' : 'outline'}>
                                                {company.has_nav_credentials
                                                    ? t('finance.companies.navConfigured', 'admin')
                                                    : t('finance.companies.navNotSet', 'admin')}
                                            </Badge>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">{formatDate(company.created_at)}</td>
                                </tr>
                            ))}
                            {companies.length === 0 && (
                                <tr>
                                    <td className="px-3 py-4 text-gray-500" colSpan={6}>
                                        {t('finance.companies.emptyTitle', 'admin')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white w-full max-w-3xl rounded shadow-lg p-6 space-y-4 my-8">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold">
                                {t('finance.companies.newTitle', 'admin')}
                            </h2>
                            <button onClick={closeForm} className="text-gray-600 hover:text-black">✕</button>
                        </div>

                        {submitError && <div className="text-red-600 text-sm">{submitError}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label htmlFor="name">{t('finance.companies.fields.name', 'admin')}</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder={t('finance.companies.placeholders.name', 'admin')}
                                        className={formErrors.name ? 'border-red-500' : ''}
                                    />
                                    {formErrors.name && <p className="text-sm text-red-600">{formErrors.name}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="tax_id">{t('finance.companies.fields.taxId', 'admin')}</Label>
                                    <Input
                                        id="tax_id"
                                        value={formData.tax_id}
                                        onChange={(e) => handleInputChange('tax_id', e.target.value)}
                                        placeholder={t('finance.companies.placeholders.taxIdNew', 'admin')}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="registration_no">{t('finance.companies.fields.registrationNo', 'admin')}</Label>
                                    <Input
                                        id="registration_no"
                                        value={formData.registration_no}
                                        onChange={(e) => handleInputChange('registration_no', e.target.value)}
                                        placeholder={t('finance.companies.placeholders.registrationNo', 'admin')}
                                    />
                                </div>

                                <div className="space-y-2 md:col-span-2">
                                    <Label htmlFor="address">{t('finance.companies.fields.address', 'admin')}</Label>
                                    <Input
                                        id="address"
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        placeholder={t('finance.companies.placeholders.addressNew', 'admin')}
                                    />
                                </div>

                                <div className="flex items-center space-x-2 md:col-span-2">
                                    <Checkbox
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => handleInputChange('is_active', checked as boolean)}
                                    />
                                    <Label htmlFor="is_active">{t('status.active', 'common')}</Label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4">
                                <Button type="button" variant="outline" onClick={closeForm}>
                                    {t('actions.cancel', 'common')}
                                </Button>
                                <Button type="submit" disabled={submitting}>
                                    {submitting
                                        ? t('finance.companies.creating', 'admin')
                                        : t('finance.companies.createAction', 'admin')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompaniesTab;
